import React, { useState, useEffect } from 'react';
import Icon from 'components/AppIcon';

const SecurityPrompt = ({ onClose, onConfirm, withdrawal }) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes
  const [isResending, setIsResending] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          onClose();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [onClose]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    
    if (code.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    const success = onConfirm(code);
    if (success === false) {
      setError('Invalid verification code. Please try again.');
      setCode('');
    }
  };

  const handleResendCode = async () => {
    setIsResending(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    setIsResending(false);
    setTimeLeft(300); // Reset timer
  };

  if (!withdrawal) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-modal p-4">
      <div className="bg-surface rounded-trading border border-border w-full max-w-md">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-warning-100 rounded-full flex items-center justify-center">
              <Icon name="Shield" size={20} className="text-warning" />
            </div>
            <div>
              <h2 className="text-lg font-heading-semibold text-text-primary">
                Security Verification
              </h2>
              <p className="text-sm text-text-secondary">Two-Factor Authentication Required</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-trading hover:bg-secondary-50 transition-colors duration-trading-fast"
          >
            <Icon name="X" size={20} className="text-text-secondary" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Withdrawal Summary */}
          <div className="p-4 bg-warning-50 border border-warning-200 rounded-trading">
            <h3 className="font-body-medium text-warning-700 mb-2">Withdrawal Request</h3>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-warning-600">Amount:</span>
                <span className="text-warning-700 font-body-medium">
                  {withdrawal.amount} {withdrawal.currency}
                </span>
              </div>
              {withdrawal.address && (
                <div className="flex justify-between">
                  <span className="text-warning-600">To:</span>
                  <span className="text-warning-700 font-data text-xs">
                    {withdrawal.address.slice(0, 10)}...{withdrawal.address.slice(-8)}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* 2FA Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-body-medium text-text-secondary mb-2">
                Enter 6-digit code from your authenticator app
              </label>
              <input
                type="text"
                value={code}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setCode(value);
                  setError('');
                }}
                placeholder="000000"
                maxLength={6}
                className={`w-full px-4 py-3 text-center text-2xl font-data tracking-widest border rounded-trading focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                  error ? 'border-error' : 'border-border'
                }`}
                autoComplete="off"
              />
              {error && (
                <p className="mt-2 text-sm text-error flex items-center space-x-1">
                  <Icon name="AlertCircle" size={14} />
                  <span>{error}</span>
                </p>
              )}
            </div>

            {/* Timer */}
            <div className="text-center">
              <p className="text-sm text-text-secondary">
                Code expires in: <span className="font-data text-warning">{formatTime(timeLeft)}</span>
              </p>
            </div>

            {/* Mock Credentials Notice */}
            <div className="p-3 bg-primary-50 border border-primary-200 rounded-trading">
              <div className="flex items-start space-x-2">
                <Icon name="Info" size={16} className="text-primary mt-0.5" />
                <div>
                  <p className="text-sm text-primary-700 font-body-medium">Demo Mode</p>
                  <p className="text-xs text-primary-600">Use code: <span className="font-data">123456</span></p>
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={code.length !== 6}
              className="w-full bg-primary text-white px-4 py-3 rounded-trading font-body-medium hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-trading-fast"
            >
              Verify & Withdraw
            </button>
          </form>

          {/* Resend Code */}
          <div className="text-center pt-4 border-t border-border">
            <p className="text-sm text-text-secondary mb-2">Didn't receive the code?</p>
            <button
              onClick={handleResendCode}
              disabled={isResending}
              className="text-primary hover:text-primary-600 font-body-medium text-sm disabled:opacity-50"
            >
              {isResending ? (
                <span className="flex items-center space-x-1">
                  <Icon name="Loader" size={14} className="animate-spin" />
                  <span>Sending...</span>
                </span>
              ) : (
                'Resend Code'
              )}
            </button>
          </div>

          {/* Security Notice */}
          <div className="p-4 bg-secondary-50 border border-secondary-200 rounded-trading">
            <div className="flex items-start space-x-3">
              <Icon name="Lock" size={16} className="text-text-secondary mt-0.5" />
              <div>
                <p className="text-sm text-text-secondary">
                  This verification helps protect your account from unauthorized withdrawals. 
                  Never share your 2FA codes with anyone.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecurityPrompt;